import { useEffect, useState } from "react";
import { Table, Button, Card, DatePicker, Upload, Input, message, Spin, Tooltip } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { ColumnType } from "antd/es/table";
import { getSavingAccounts, uploadSavingAccountExcel, exportSavingAccountExcel } from "../../utils/API";
import { BiExport as BiExportIcon } from "react-icons/bi";

const SavingAccountPage = () => {
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [fromDate, setFromDate] = useState<dayjs.Dayjs | null>(null);
  const [toDate, setToDate] = useState<dayjs.Dayjs | null>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res: any = await getSavingAccounts();
      if (res?.data) setDataSource(res.data);
      else setDataSource([]);
    } catch (err) {
      message.error("Failed to fetch saving account data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const res: any = await uploadSavingAccountExcel(file);
      const imported = res?.data?.imported ?? res?.imported;
      message.success(`${imported ?? 0} rows imported`);
      fetchData();
    } catch (err: any) {
      message.error(err?.response?.data?.message || "Failed to import Excel");
    } finally {
      setUploading(false);
    }
    return false;
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params: any = {};
      if (fromDate) params.fromDate = fromDate.format("DD-MM-YYYY");
      if (toDate) params.toDate = toDate.format("DD-MM-YYYY");
      const response: any = await exportSavingAccountExcel(params);
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "SavingAccount.xlsx";
      link.click();
      window.URL.revokeObjectURL(url);
      message.success("Excel exported successfully");
    } catch (err) {
      message.error("Failed to export Excel");
    } finally {
      setExporting(false);
    }
  };

  const getTimestamp = (dateStr: string) => {
    if (!dateStr) return 0;
    const p = dayjs(dateStr, ["DD-MM-YYYY", "D-M-YYYY", "YYYY-MM-DD"], true);
    return p.isValid() ? p.valueOf() : 0;
  };

  const filteredData = dataSource.filter((item) => {
    const search = searchText.toLowerCase();
    const searchData =
      (item.description?.toLowerCase() || "").includes(search) ||
      (item.reference?.toLowerCase() || "").includes(search) ||
      (item.type?.toLowerCase() || "").includes(search);
    const fromOk = fromDate ? getTimestamp(item.date) >= fromDate.startOf("day").valueOf() : true;
    const toOk = toDate ? getTimestamp(item.date) <= toDate.endOf("day").valueOf() : true;
    return searchData && fromOk && toOk;
  });

  const columns: ColumnType<any>[] = [
    { title: "Date", dataIndex: "date", key: "date" },
    { title: "Description", dataIndex: "description", key: "description" },
    { title: "Type", dataIndex: "type", key: "type" },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (v: number) => (
        <span style={{ color: v < 0 ? "#ef4444" : "#059669", fontWeight: 600 }}>
          {Math.abs(v).toLocaleString("en-IN")}
        </span>
      ),
    },
    {
      title: "Balance",
      dataIndex: "balance",
      key: "balance",
      render: (v: any) => (v ?? v === 0 ? Number(v).toLocaleString("en-IN") : "-"),
    },
    { title: "Reference", dataIndex: "reference", key: "reference" },
  ];

  return (
    <div style={{ padding: "20px" }}>
      <Card
        style={{ borderRadius: "12px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}
        title={<span style={{ fontSize: "20px", fontWeight: 600 }}>Personal Saving Account</span>}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: 16, flexWrap: "wrap", justifyContent: "flex-end" }}>
          <DatePicker.RangePicker
            format="DD-MM-YYYY"
            placeholder={["From Date", "To Date"]}
            value={[fromDate, toDate]}
            onChange={(dates) => {
              setFromDate(dates ? dates[0] : null);
              setToDate(dates ? dates[1] : null);
            }}
            allowClear
            style={{ height: "40px", borderRadius: "8px" }}
          />
          <Input
            placeholder="Search..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            style={{ width: "220px", height: "40px" }}
          />
          <Upload accept=".xlsx,.xls,.csv" showUploadList={false} beforeUpload={handleUpload}>
            <Button
              loading={uploading}
              style={{ background: "#001529", color: "#fff", height: "40px", borderRadius: "8px", display: "flex", alignItems: "center", gap: "6px" }}
            >
              <InboxOutlined />
              Upload Excel
            </Button>
          </Upload>
          <Tooltip title="Export to Excel" color="#CCCCCC">
            <Button
              onClick={handleExport}
              loading={exporting}
              style={{ background: "#001529", color: "#fff", height: "40px", width: "40px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              {!exporting && <BiExportIcon size={18} />}
            </Button>
          </Tooltip>
        </div>

        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={filteredData}
            rowKey={(r) => r._id || `${r.date}-${r.reference}`}
            pagination={{ pageSize: 10, showSizeChanger: false }}
            scroll={{ x: "max-content" }}
          />
        </Spin>
      </Card>
    </div>
  );
};

export default SavingAccountPage;


